if (!customElements.get('td-customization-studio')) {
  customElements.define('td-customization-studio', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.form = this.querySelector('[data-form]');
      if (!this.form) return;
      this.productChoices = [...this.querySelectorAll('[data-product-choice]')];
      this.variantSelect = this.querySelector('[data-variant-select]');
      this.variantInput = this.querySelector('[data-variant-id]');
      this.engraving = this.querySelector('[data-engraving]');
      this.font = this.querySelector('[data-font]');
      this.colorChoices = [...this.querySelectorAll('[data-color]')];
      this.upload = this.querySelector('[data-upload]');
      this.removeUpload = this.querySelector('[data-remove-upload]');
      this.scale = this.querySelector('[data-scale]');
      this.error = this.querySelector('[data-error]');
      this.summary = this.querySelector('[data-selection-summary]');
      this.products = new Map();
      this.activeLayer = 'text';
      this.layerState = { text: { x: 50, y: 36, scale: 100 }, image: { x: 50, y: 57, scale: 100 } };
      this.querySelectorAll('[data-product-json]').forEach((script) => {
        try { this.products.set(script.dataset.productJson, JSON.parse(script.textContent)); } catch (error) { console.error(error); }
      });
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-product-choice]')) this.selectProduct(event.target.value);
        if (event.target === this.variantSelect) this.selectVariant();
        if (event.target.matches('[data-color]')) this.updateText();
        if (event.target === this.upload) this.readUpload();
      });
      this.addEventListener('input', (event) => {
        if (event.target === this.engraving) this.updateText();
        if (event.target === this.scale) this.setScale(Number(event.target.value));
      });
      this.addEventListener('click', (event) => this.handleClick(event));
      this.addEventListener('pointerdown', (event) => this.startDrag(event));
      this.addEventListener('keydown', (event) => this.nudgeLayer(event));
      this.font?.addEventListener('change', () => this.updateText());
      this.form.addEventListener('submit', (event) => this.validate(event));
      const selected = this.productChoices.find((choice) => choice.checked) || this.productChoices[0];
      if (selected) { selected.checked = true; this.selectProduct(selected.value); }
      this.selectLayer('text');
      this.updateText();
      this.applyLayerState();
    }

    handleClick(event) {
      if (event.target.closest('[data-clean-preview]')) return this.clearSelection();
      if (event.target.closest('[data-remove-engraving]')) {
        this.engraving.value = '';
        this.updateText();
        if (this.upload?.files?.length) this.selectLayer('image');
        else this.engraving.focus();
        return;
      }
      const selectLayer = event.target.closest('[data-select-layer]');
      if (selectLayer && !selectLayer.disabled) return this.selectLayer(selectLayer.dataset.selectLayer);
      const editorTab = event.target.closest('[data-editor-tab]');
      if (editorTab) return this.selectLayer(editorTab.dataset.editorTab);
      const layer = event.target.closest('[data-layer-item]');
      if (layer) return this.selectLayer(layer.dataset.layerItem);
      const preset = event.target.closest('[data-position-preset]');
      if (preset) return this.setPosition(Number(preset.dataset.x), Number(preset.dataset.y));
      if (event.target.closest('[data-remove-upload]')) return this.clearUpload();
      const mode = event.target.closest('[data-mode-toggle]');
      if (mode) return this.toggleMode(mode);
      if (event.target.closest('[data-preview]') && !this.dragMoved) this.clearSelection();
    }

    selectProduct(handle) {
      const product = this.products.get(handle);
      if (!product) return;
      this.activeProduct = product;
      this.querySelectorAll('[data-product-preview]').forEach((preview) => preview.classList.toggle('is-hidden', preview.dataset.productPreview !== handle));
      const current = this.querySelector('[data-current-product]');
      if (current) current.textContent = product.title;
      this.variantSelect.innerHTML = '';
      product.variants.forEach((variant) => {
        const option = document.createElement('option');
        option.value = String(variant.id);
        option.textContent = `${variant.title} — ${variant.price}${variant.available ? '' : ' — Sold out'}`;
        option.disabled = !variant.available;
        this.variantSelect.append(option);
      });
      const firstAvailable = product.variants.find((variant) => variant.available) || product.variants[0];
      if (firstAvailable) this.variantSelect.value = String(firstAvailable.id);
      const preview = this.activePreview();
      const mode = this.querySelector('[data-mode-toggle]');
      if (mode) { mode.hidden = preview?.dataset.hasModel !== 'true'; mode.textContent = 'Rotate 3D'; }
      this.querySelectorAll('[data-design-surface]').forEach((surface) => surface.classList.remove('is-rotate-mode'));
      this.selectVariant();
      this.applyLayerState();
    }

    selectVariant() {
      const variant = this.activeProduct?.variants.find((item) => String(item.id) === this.variantSelect.value);
      if (!variant) return;
      this.variantInput.value = variant.id;
      const image = this.activePreview()?.querySelector('.td-studio__product-image');
      if (image && variant.image && image.src !== variant.image) { image.removeAttribute('srcset'); image.src = variant.image; }
      const price = this.querySelector('[data-cart-price]');
      if (price) price.textContent = variant.price;
      this.updateSummary();
    }

    clearSelection() {
      this.querySelectorAll('[data-layer-item]').forEach((layer) => {
        layer.classList.remove('is-selected');
        if (layer === document.activeElement) layer.blur();
      });
      this.querySelectorAll('[data-select-layer]').forEach((button) => {
        button.classList.remove('is-active');
        button.setAttribute('aria-pressed', 'false');
      });
      const button = this.querySelector('[data-clean-preview]');
      button?.classList.add('is-active');
      button?.setAttribute('aria-pressed', 'true');
      const hint = this.querySelector('.td-studio__stage-bar p');
      if (hint) hint.hidden = true;
    }

    selectLayer(name) {
      const previewButton = this.querySelector('[data-clean-preview]');
      previewButton?.classList.remove('is-active');
      previewButton?.setAttribute('aria-pressed', 'false');
      const hint = this.querySelector('.td-studio__stage-bar p');
      if (hint) hint.hidden = false;
      if (name === 'image' && !this.upload?.files?.length) {
        this.setEditorTab('image');
        const panel = this.querySelector('[data-editor-panel="image"]');
        panel?.scrollIntoView({ behavior: 'auto', block: 'center' });
        this.upload?.focus({ preventScroll: true });
        return;
      }
      this.activeLayer = name;
      this.querySelectorAll('[data-layer-item]').forEach((layer) => layer.classList.toggle('is-selected', layer.dataset.layerItem === name));
      this.querySelectorAll('[data-select-layer]').forEach((button) => {
        const active = button.dataset.selectLayer === name;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      this.setEditorTab(name);
      const state = this.layerState[name];
      if (this.scale) this.scale.value = String(state.scale);
      const label = name === 'text' ? 'Engraving' : 'Artwork';
      this.querySelectorAll('[data-active-object-label]').forEach((node) => { node.textContent = label; });
      const placement = this.querySelector('[data-placement-label]');
      if (placement) placement.textContent = label;
      this.updatePositionFields();
    }

    setEditorTab(name) {
      this.querySelectorAll('[data-editor-tab]').forEach((tab) => {
        const active = tab.dataset.editorTab === name;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      this.querySelectorAll('[data-editor-panel]').forEach((panel) => { panel.hidden = panel.dataset.editorPanel !== name; });
    }

    updateText() {
      const text = this.engraving?.value.trim() || '';
      const isPlaceholder = !this.engraving?.value.trim();
      this.querySelectorAll('[data-character-count]').forEach((node) => { node.textContent = String(this.engraving?.value.length || 0); });
      const fonts = { 'Gilroy Bold': 'var(--font-heading-family, sans-serif)', 'Gilroy Black Italic': 'var(--font-heading-family, sans-serif)', 'Classic Sans': 'Helvetica Neue, Helvetica, sans-serif', 'Monogram Serif': 'Georgia, serif' };
      this.querySelectorAll('[data-mark-text]').forEach((node) => {
        node.textContent = text;
        node.style.color = this.colorChoices.find((input) => input.checked)?.dataset.color || '#adc3d8';
        node.style.fontFamily = fonts[this.font?.value] || fonts['Gilroy Bold'];
        node.style.fontStyle = this.font?.value === 'Gilroy Black Italic' ? 'italic' : 'normal';
        node.style.fontWeight = this.font?.value === 'Gilroy Black Italic' ? '900' : '700';
      });
      this.querySelectorAll('[data-layer-item="text"]').forEach((layer) => {
        layer.hidden = isPlaceholder;
        layer.classList.remove('is-placeholder');
      });
      const remove = this.querySelector('[data-remove-engraving]');
      if (remove) remove.hidden = isPlaceholder;
      this.querySelectorAll('[name^="properties[Engraving"]').forEach((input) => {
        if (input !== this.engraving) input.disabled = isPlaceholder;
      });
      this.updateSummary();
    }

    readUpload() {
      const file = this.upload?.files?.[0];
      if (!file) return this.clearUpload(false);
      if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) { this.showError('Please upload a PNG, JPG, or WebP image.'); this.clearUpload(); return; }
      if (file.size > 12 * 1024 * 1024) { this.showError('This image is larger than 12 MB. Choose a smaller file and try again.'); this.clearUpload(); return; }
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        this.querySelectorAll('[data-layer-item="image"]').forEach((layer) => {
          let image = layer.querySelector('[data-mark-image]');
          if (!image) {
            image = document.createElement('img');
            image.dataset.markImage = '';
            image.alt = 'Uploaded artwork preview';
            image.width = 160;
            image.height = 100;
            layer.prepend(image);
          }
          image.src = reader.result;
          layer.hidden = false;
        });
        const button = this.querySelector('[data-select-layer="image"]');
        if (button) button.disabled = false;
        const name = this.querySelector('[data-upload-name]');
        if (name) name.textContent = file.name;
        if (this.removeUpload) this.removeUpload.hidden = false;
        this.hideError(); this.selectLayer('image'); this.updateSummary();
      });
      reader.readAsDataURL(file);
    }

    clearUpload(resetInput = true) {
      if (resetInput && this.upload) this.upload.value = '';
      this.querySelectorAll('[data-mark-image]').forEach((image) => image.remove());
      this.querySelectorAll('[data-layer-item="image"]').forEach((layer) => { layer.hidden = true; });
      const button = this.querySelector('[data-select-layer="image"]');
      if (button) button.disabled = false;
      const name = this.querySelector('[data-upload-name]');
      if (name) name.textContent = 'PNG, JPG or WebP · up to 12 MB';
      if (this.removeUpload) this.removeUpload.hidden = true;
      if (this.activeLayer === 'image') this.selectLayer('text');
      this.updateSummary();
    }

    startDrag(event) {
      const layer = event.target.closest('[data-layer-item]');
      if (!layer || layer.closest('.is-hidden') || event.isPrimary === false || event.button > 0) return;
      event.preventDefault(); this.selectLayer(layer.dataset.layerItem);
      const surface = layer.closest('[data-design-surface]');
      const rect = surface.getBoundingClientRect();
      const pointerId = event.pointerId;
      const origin = { ...this.layerState[this.activeLayer] };
      const startX = event.clientX;
      const startY = event.clientY;
      this.dragMoved = false; layer.setPointerCapture(pointerId);
      const move = (moveEvent) => {
        if (moveEvent.pointerId !== pointerId) return;
        this.dragMoved = true;
        this.setPosition(origin.x + ((moveEvent.clientX - startX) / rect.width) * 100, origin.y + ((moveEvent.clientY - startY) / rect.height) * 100);
      };
      const end = () => { layer.removeEventListener('pointermove', move); layer.removeEventListener('pointerup', end); layer.removeEventListener('pointercancel', end); setTimeout(() => { this.dragMoved = false; }, 0); };
      layer.addEventListener('pointermove', move); layer.addEventListener('pointerup', end); layer.addEventListener('pointercancel', end);
    }

    nudgeLayer(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.clearSelection();
        return;
      }
      const tab = event.target.closest('[data-editor-tab]');
      if (tab && ['ArrowLeft', 'ArrowRight'].includes(event.key)) {
        event.preventDefault();
        const tabs = [...this.querySelectorAll('[data-editor-tab]')];
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        const next = tabs[(tabs.indexOf(tab) + direction + tabs.length) % tabs.length];
        this.selectLayer(next.dataset.editorTab);
        next.focus();
        return;
      }
      const layer = event.target.closest('[data-layer-item]');
      if (!layer || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
      event.preventDefault(); this.selectLayer(layer.dataset.layerItem);
      const step = event.shiftKey ? 5 : 1;
      const state = this.layerState[this.activeLayer];
      this.setPosition(state.x + (event.key === 'ArrowRight' ? step : event.key === 'ArrowLeft' ? -step : 0), state.y + (event.key === 'ArrowDown' ? step : event.key === 'ArrowUp' ? -step : 0));
    }

    setPosition(x, y) {
      const state = this.layerState[this.activeLayer];
      const preview = this.activePreview();
      const surface = preview?.querySelector('[data-design-surface]');
      const layer = preview?.querySelector(`[data-layer-item="${this.activeLayer}"]`);
      const surfaceRect = surface?.getBoundingClientRect();
      const layerRect = layer?.getBoundingClientRect();
      const edgeX = surfaceRect?.width && layerRect?.width ? Math.min(42, (layerRect.width / surfaceRect.width) * 50 + 1) : 5;
      const edgeY = surfaceRect?.height && layerRect?.height ? Math.min(42, (layerRect.height / surfaceRect.height) * 50 + 1) : 7;
      state.x = Math.max(edgeX, Math.min(100 - edgeX, Math.round(x * 10) / 10));
      state.y = Math.max(edgeY, Math.min(100 - edgeY, Math.round(y * 10) / 10));
      this.applyLayerState();
    }
    setScale(value) {
      const state = this.layerState[this.activeLayer];
      state.scale = value;
      this.applyLayerState();
      this.setPosition(state.x, state.y);
    }
    applyLayerState() {
      Object.entries(this.layerState).forEach(([name, state]) => {
        this.querySelectorAll(`[data-layer-item="${name}"]`).forEach((layer) => { layer.style.setProperty('--x', `${state.x}%`); layer.style.setProperty('--y', `${state.y}%`); layer.style.setProperty('--scale', state.scale / 100); });
      });
      this.updatePositionFields();
    }
    updatePositionFields() {
      const state = this.layerState[this.activeLayer];
      if (!state) return;
      const readout = this.querySelector('[data-position-readout]');
      const scaleOutput = this.querySelector('[data-scale-output]');
      if (readout) readout.textContent = `${state.x}% × ${state.y}%`;
      if (scaleOutput) scaleOutput.textContent = `${state.scale}%`;
      Object.entries(this.layerState).forEach(([name, values]) => {
        const positionInput = this.querySelector(`[data-position-input="${name}"]`);
        const scaleInput = this.querySelector(`[data-scale-input="${name}"]`);
        if (positionInput) positionInput.value = `${values.x}%, ${values.y}%`;
        if (scaleInput) scaleInput.value = `${values.scale}%`;
      });
    }

    toggleMode(button) {
      const surface = this.activePreview()?.querySelector('[data-design-surface]');
      if (!surface) return;
      const rotating = surface.classList.toggle('is-rotate-mode');
      button.textContent = rotating ? 'Place design' : 'Rotate 3D';
    }
    activePreview() { return this.activeProduct ? this.querySelector(`[data-product-preview="${CSS.escape(this.activeProduct.handle)}"]`) : null; }
    updateSummary() {
      if (!this.summary || !this.activeProduct) return;
      const variant = this.activeProduct.variants.find((item) => String(item.id) === this.variantSelect.value);
      const details = [this.activeProduct.title, variant?.title !== 'Default Title' ? variant?.title : '', this.engraving?.value.trim() ? 'engraving added' : '', this.upload?.files?.length ? 'artwork added' : ''].filter(Boolean);
      this.summary.textContent = details.join(' · ');
    }
    validate(event) {
      this.hideError();
      const variant = this.activeProduct?.variants.find((item) => String(item.id) === this.variantInput.value);
      if (!variant?.available) { event.preventDefault(); this.showError('Please choose an available product option.'); this.variantSelect.focus(); return; }
      if (!this.engraving?.value.trim() && !this.upload?.files?.length) { event.preventDefault(); this.showError('Add engraving text or upload an image before adding this customization.'); this.engraving?.focus(); return; }
      const button = this.querySelector('[data-add-to-cart]');
      if (button) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        const label = button.querySelector('span');
        if (label) label.textContent = 'Adding your piece…';
      }
    }
    showError(message) { if (this.error) { this.error.textContent = message; this.error.hidden = false; } }
    hideError() { if (this.error) this.error.hidden = true; }
  });
}
